import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { post } from "aws-amplify/api";

async function readErrorBody(error) {
  const candidates = [
    error?.response?.body,
    error?.response,
    error?.underlyingError?.response?.body,
    error?.cause?.response?.body,
    error?.body,
  ];
  for (const src of candidates) {
    if (src == null) continue;
    if (typeof src === "object" && (src.error || src.detail || src.reference)) return src;
    if (typeof src?.json === "function") {
      try {
        return await Promise.race([
          src.json(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
      } catch (_) { /* */ }
    }
    if (typeof src?.text === "function") {
      try {
        const text = await Promise.race([
          src.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        if (typeof text === "string" && text) return JSON.parse(text);
      } catch (_) { /* */ }
    }
    if (typeof src === "string" && src) {
      try { return JSON.parse(src); } catch (_) { /* */ }
    }
  }
  return null;
}

export default function StepUpDialog({ open, onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notEnrolled, setNotEnrolled] = useState(false);

  const reset = () => {
    setCode("");
    setBusy(false);
    setError("");
    setNotEnrolled(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError("Code must be 6 digits.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const op = post({
        apiName: "license",
        path: "/step-up/verify",
        options: { body: { code } }
      });
      await op.response;
      reset();
      onClose?.();
      // Hand control back to the caller — they re-run the original
      // write action now that we have an elevated session.
      onVerified?.();
    } catch (e) {
      const errorBody = await readErrorBody(e);
      if (errorBody?.error === "NOT_ENROLLED") {
        setNotEnrolled(true);
      } else if (errorBody?.error === "INVALID_CODE") {
        setError("That code didn't match. Try again.");
        setCode("");
      } else if (errorBody?.error === "INVALID_TOKEN") {
        setError("Your sign-in has expired — refresh and try again.");
      } else if (errorBody?.detail) {
        setError(errorBody.detail);
      } else if (errorBody?.error) {
        setError(errorBody.error);
      } else {
        setError("Verification failed. Open the devtools console for details.");
        // eslint-disable-next-line no-console
        console.error("step_up_verify failed", { errorBody, error: e });
      }
    } finally {
      setBusy(false);
    }
  };

  if (notEnrolled) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Set up MFA first</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            Editing licences and creating organisations require a one-time MFA code from an authenticator app — and you haven't enrolled yet. Set it up now (it takes about 30 seconds).
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            component={RouterLink}
            to="/admin/setup-mfa"
            variant="contained"
            startIcon={<LockResetIcon />}
            onClick={handleClose}
          >
            Set up MFA
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Confirm with MFA</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText sx={{ color: "text.primary" }}>
            Enter the 6-digit code from your authenticator app to confirm this action.
          </DialogContentText>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            label="Authentication code"
            placeholder="123456"
            autoFocus
            fullWidth
            inputProps={{
              inputMode: "numeric",
              autoComplete: "one-time-code",
              pattern: "[0-9]*",
              maxLength: 6
            }}
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.length === 6) submit();
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Codes are valid for 30 seconds. Once verified you'll have 15 minutes to make changes before being asked again.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || code.length !== 6}>
          {busy ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {busy ? "Verifying…" : "Verify"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
